import { useI18n, Trans } from '~/common/context';
import { RecordValues } from '@wyz/types';
import { Button, Card, Container, Flex, Text, WizardSteps } from '@wyz/ui';
import * as React from 'react';
import {
  RedistributionApplicationStep,
  RedistributionPricingStep,
} from '~/purchase/components';
import { RedistributionPricingPolicyHelpers } from '~/purchase/helpers';
import { usePolicyFormStepper } from '~/purchase/hooks';
import {
  PRICING_STATUS_ENUM,
  REDISTRIBUTION_PRICING_POLICY_FORM_STEPS,
} from '~/purchase/schemas';
import {
  IGlobalRedistributionPricingFormData,
  IPricingStatus,
  RedistributionPricingPolicyFormStep,
} from '~/purchase/types';
type Step = RecordValues<
  typeof RedistributionPricingPolicyHelpers.REDISTRIBUTION_PRICING_POLICY_STEP_INDEX
>;

type Props = {
  onSubmit: (
    formData: IGlobalRedistributionPricingFormData,
    status: IPricingStatus,
  ) => void;
  isSubmitting: boolean;
  defaultValues: IGlobalRedistributionPricingFormData;
  policyGroupId?: number;
};
function RedistributionPricingPolicyForm({
  onSubmit,
  isSubmitting,
  defaultValues,
  policyGroupId,
}: Props) {
  const { t } = useI18n();
  const [formState, setFormState] =
    React.useState<IGlobalRedistributionPricingFormData>(defaultValues);

  const [currentStepIndex, stepperHandlers] = usePolicyFormStepper<Step>({});
  const currentStepKey =
    REDISTRIBUTION_PRICING_POLICY_FORM_STEPS[currentStepIndex];

  return (
    <>
      <WizardSteps
        steps={(
          Object.keys(
            RedistributionPricingPolicyHelpers.REDISTRIBUTION_PRICING_POLICY_STEP_INDEX,
          ) as Array<RedistributionPricingPolicyFormStep>
        ).map((stepKey) => ({
          id: stepKey,
          label: t(
            RedistributionPricingPolicyHelpers
              .REDISTRIBUTION_PRICING_POLICY_STEP_LABELS[stepKey],
          ),
        }))}
        activeStep={currentStepIndex}
        onSelectStep={() => {}} // no switch between step using the wizard, only the button prev/next should be used, otherwise, we won't validate the form correctly
      />
      <Container>
        {currentStepIndex ==
          RedistributionPricingPolicyHelpers
            .REDISTRIBUTION_PRICING_POLICY_STEP_INDEX.APPLICATION && (
          <RedistributionApplicationStep
            globalFormData={formState}
            policyGroupId={policyGroupId}
            onSubmit={(formData) => {
              stepperHandlers.onNext();
              setFormState((prev) => ({
                ...prev,
                APPLICATION: formData,
              }));
            }}
          />
        )}

        {currentStepIndex ==
          RedistributionPricingPolicyHelpers
            .REDISTRIBUTION_PRICING_POLICY_STEP_INDEX.PRICING && (
          <RedistributionPricingStep
            globalFormData={formState}
            onSubmit={(formData, status) => {
              setFormState((prev) => {
                const nextState = { ...prev, PRICING: formData };
                onSubmit(nextState, status);
                return nextState;
              });
            }}
          />
        )}

        <Card className='sticky' style={{ bottom: '5%' }}>
          <Card.Body>
            <Flex justifyContent={'between'}>
              <Button
                type='button'
                id='previous'
                variant='link'
                disabled={
                  isSubmitting ||
                  currentStepIndex ===
                    RedistributionPricingPolicyHelpers.FIRST_STEP
                }
                onClick={stepperHandlers.onPrevious}
              >
                <Trans i18nKey='app.common.button.previous' />
              </Button>
              <Flex className='space-x-2' alignItems='center'>
                <Text heading='sm' color='secondary'>
                  {formState.PRICING?.name}
                </Text>
                <Text heading='sm' color='secondary-gray'>
                  <Trans
                    i18nKey='app.common.step_value'
                    values={{
                      stepNumber: currentStepIndex + 1,
                      stepName: t(
                        RedistributionPricingPolicyHelpers
                          .REDISTRIBUTION_PRICING_POLICY_STEP_LABELS[
                          currentStepKey
                        ],
                      ),
                    }}
                  />
                </Text>
                {currentStepIndex ===
                  RedistributionPricingPolicyHelpers.LAST_STEP && (
                  <Button
                    id={'save-as-draft'}
                    data-status={PRICING_STATUS_ENUM.DRAFT}
                    variant='secondary'
                    form={currentStepIndex.toString()}
                    type='submit'
                    isLoading={isSubmitting}
                    children={
                      <Trans i18nKey='app.common.button.save_as_draft' />
                    }
                  />
                )}
                <Button
                  id='next'
                  data-status={PRICING_STATUS_ENUM.ENABLED}
                  variant='primary'
                  type='submit'
                  form={currentStepIndex.toString()}
                  isLoading={isSubmitting}
                  children={
                    currentStepIndex <
                    RedistributionPricingPolicyHelpers.LAST_STEP
                      ? t('app.common.buttons.continue')
                      : t('app.common.button.save')
                  }
                />
              </Flex>
            </Flex>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}
export default RedistributionPricingPolicyForm;
